/* eslint-disable @next/next/no-img-element */
import React, {
  memo, useState, useRef, useCallback,
} from 'react';
import IconFont from '$/components/IconFont';
import CommonExposure from '$/components/CommonExposure';
import { handleImageUrl } from '$/utils/image';
import { mtstatTrack } from '$/utils/mtstat';
import classNames from 'classnames';
import { V3CanvasHomeCaseItem } from '$/domains/caseV3/type';
import { formatPlatformNum } from '../../utils';
import styles from './index.module.scss';

export type PromptCaseCardProps = {
  item: V3CanvasHomeCaseItem;
  positionId: number;
  categoryId: number;
  tag: string;
  onCardClick?: (item: V3CanvasHomeCaseItem) => void;
  onTryButtonClick?: (item: V3CanvasHomeCaseItem) => void;
  onLikeClick?: (item: V3CanvasHomeCaseItem) => void;
};

/**
 * 渲染右侧单张 prompt 案例卡片。
 */
function PromptCaseCard({
  item,
  positionId,
  categoryId,
  tag,
  onCardClick,
  onLikeClick,
  onTryButtonClick,
}: PromptCaseCardProps) {
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const videoUrl = item.work_media?.type === 'video' ? item.work_media.url : '';

  /** 组装首页精选案例新版埋点参数，字段类型按埋点文档统一转成 string。 */
  const getHomecaseTrackParams = useCallback(() => ({
    case_id: `${item.case_id}`,
    position: `${positionId}`,
    category_id: `${categoryId}`,
    category_name: tag,
  }), [categoryId, item.case_id, tag, positionId]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    if (videoUrl && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [videoUrl]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  /**
   * 点击案例卡片时埋点并把 prompt 内容同步到首页输入框。
   */
  const handleCardClick = () => {
    mtstatTrack('roboneo_homecase_click', {
      ...getHomecaseTrackParams(),
      click_type: 'card',
    });

    onCardClick?.(item);
  };

  return (
    <CommonExposure
      className={classNames(styles.FeaturedCase, { [styles.hovered]: hovered })}
      onClick={handleCardClick}
      onExposed={() => {
        mtstatTrack('roboneo_homecase_exp', getHomecaseTrackParams());
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        className={styles.caseCover}
        src={handleImageUrl(item.cover_media.url, { mode: 3, width: 220, height: 290 })}
        alt=""
        loading="lazy"
        crossOrigin="anonymous"
      />
      {videoUrl && (
        <video
          ref={videoRef}
          className={styles.caseVideo}
          src={item.work_media.url}
          muted
          loop
          playsInline
          preload="none"
          crossOrigin="anonymous"
        />
      )}
      <div className={styles.casePreviewInfo}>
        {item.work_media.type === 'video' && <IconFont name="playCircleFill" fontSize={16} className={styles.playIcon} />}
        <span
          className={styles.hotBadge}
        >
          <IconFont name="fireV2Fill" fontSize={12} />
          <span>{formatPlatformNum(item.click_num || 0)}</span>
        </span>
      </div>
      <div className={styles.caseInfo}>
        <span className={styles.caseTitle}>{item.title}</span>
        <span className={styles.caseDesc}>{item.desc}</span>
      </div>

    </CommonExposure>
  );
}

export default memo(PromptCaseCard);
